/**
 * Bridge between Unity native (C#) and the embedded Timeline web app.
 *
 * Native → Web:  C# calls evaluateJavaScript("window.__previsRecv({...})")
 * Web → Native:  JS navigates to previspro://bridge?msg=<encoded JSON>
 */

import { useAccountStore } from '@/store/accountStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { markTimelineDismissed } from '@/telemetry/timelineLifecycle';
import { sendToNative, enqueueSendToNative, diagLog, sendDirectNavigation } from './transport';
import { registerFeatureMaps, resetFeatureMaps, dispatchMessage, assertBridgeTableComplete } from './dispatch';
import { settingsHandlers } from './features/settings';
import { avatarHandlers } from './features/avatar';
import { audioHandlers } from './features/audio';
import { screenplayHandlers } from './features/screenplay';
import { shareHandlers } from './features/share';
import { purchasingHandlers } from './features/purchasing';
import { authHandlers } from './features/auth';
import { timelineHandlers, setOnProjectLoaded } from './features/timeline';
import { fullImageHandlers } from './features/fullImage';
import { castHeadshotsHandlers } from './features/castHeadshots';
import { imagePickHandlers } from './features/imagePick';
import { assetsHandlers, installAssetSweepTriggers } from './features/assets';
import { blockingHandlers } from './features/blocking';
import { sceneSettingsHandlers } from './features/sceneSettings';
import { shotSettingsHandlers } from './features/shotSettings';
import { projectSettingsHandlers } from './features/projectSettings';
import { setsHandlers } from './features/sets';
import { tutorialHandlers } from './features/tutorial';
// 20260928 - the canvas overlay's four inbound verbs (showView / setOverlayContext /
// setOverlayRegion / setWhatsNewBadge). A leaf-store module; see its header.
import { canvasOverlayHandlers } from './features/canvasOverlay';
// 20261005 - the canvas Coverage overview's one inbound verb (coverageShotLens). No store; it hands
// the report to the page's registered listener.
import { canvasCoverageHandlers } from './features/canvasCoverage';
import type { BridgeHandlers } from './messages';

// Re-export the transport surface so the existing consumers of embeddedBridge keep
// importing sendToNative / enqueueSendToNative / diagLog from here unchanged.
export { sendToNative, enqueueSendToNative, diagLog };

// Re-export the share/export senders + public result type so existing consumers
// (ShareDialog, EarnCreditsDialog, nleExport, htmlExporter) import from here unchanged.
export {
  requestTeamUpload, requestTeamUpdate, requestSocialShare,
  requestExportForShare, bridgeExportNLE, bridgeExportHtml,
} from './features/share';
export type { TeamShareResult } from './features/share';

// Re-export the purchasing senders so existing consumers (TokenPurchaseDialog,
// WelcomeDepletionDialog) import from here unchanged.
export { requestCreditPurchase, setPendingPackDetails } from './features/purchasing';

// Re-export the auth sender so the existing consumer (supabaseClient.ts) imports
// from here unchanged.
export { requestTokenRefresh } from './features/auth';

// ─── Receive from native ────────────────────────────────────────────────

let bridgeReady = false;
let bridgeAbort: AbortController | null = null;

// Compose the inbound handler table at module load — the merged TABLE is complete
// before any window.__previsRecv is ever assigned (initBridge asserts this below).
// Each feature module owns its verbs; the legacy inbound switch that once handled
// them here was deleted in Bridge 12/14 (Task 13).
registerFeatureMaps([settingsHandlers, avatarHandlers, audioHandlers, screenplayHandlers, shareHandlers, purchasingHandlers, authHandlers, timelineHandlers, fullImageHandlers, castHeadshotsHandlers, imagePickHandlers, assetsHandlers, blockingHandlers, sceneSettingsHandlers, shotSettingsHandlers, projectSettingsHandlers, setsHandlers, tutorialHandlers, canvasOverlayHandlers, canvasCoverageHandlers]);

// ── Vite HMR (dev only; import.meta.hot is undefined in a production build) ─────────────────
// The bridge is a process-wide singleton: the handler TABLE in dispatch.ts, window.__previsRecv,
// the sceneReady buffer, initBridge's `bridgeReady` latch and the subscriptions it installs. A hot
// update of this module — or of ANY feature module beneath it, since none of them accepts and the
// update walks up to the first importer that does — re-evaluates this file on top of that live
// state. Without these hooks that meant: registerFeatureMaps() above threw "duplicate handler for
// 'setUIScaling'" (dispatch.ts keeps its table — it is a dependency, not an importer), Vite logged
// "Failed to reload" and abandoned the update mid-graph, and React Fast Refresh then re-rendered
// into a half-evaluated module graph — "ReferenceError: Cannot access 'useUiStore' before
// initialization" in whatever page was open. The localisation editor's live preview died exactly
// this way on 20260903, from a checkin that touched features/screenplay.ts while it was open.
// dispose() empties the table so the NEW module can evaluate cleanly; accept() then reloads the
// page rather than double-installing every side effect above — a full reload is the only honest
// recovery for a singleton with this much live state, and every dev page re-bootstraps from a
// reload anyway (the preview harness re-fetches its fixture; the editor re-sends its state on
// i18n:ready).
if (import.meta.hot) {
  import.meta.hot.dispose(() => { resetFeatureMaps(); });
  import.meta.hot.accept(() => { window.location.reload(); });
}

// Compile-time exhaustiveness anchor. Spreading the 20 feature handler maps and
// asserting `satisfies BridgeHandlers` fails to typecheck if any BridgeMessage verb
// lacks a handler (a whole feature missing from the spread, or a feature not covering
// its own sub-union) — or if a handler exists for a verb with no message type. This is
// the compile-time suspenders; assertBridgeTableComplete() (called in initBridge) is
// the runtime belt. Kept SEPARATE from the registerFeatureMaps() call above on purpose:
// registerFeatureMaps takes the 20 maps individually so it can throw on a duplicate verb
// across features ("one verb, one owner"); a single merged spread would silently let a
// later feature overwrite an earlier verb instead.
const _bridgeHandlersExhaustive = {
  ...settingsHandlers,
  ...avatarHandlers,
  ...audioHandlers,
  ...screenplayHandlers,
  ...shareHandlers,
  ...purchasingHandlers,
  ...authHandlers,
  ...timelineHandlers,
  ...fullImageHandlers,
  ...castHeadshotsHandlers,
  ...imagePickHandlers,
  ...assetsHandlers,
  ...blockingHandlers,
  ...sceneSettingsHandlers,
  ...shotSettingsHandlers,
  ...projectSettingsHandlers,
  ...setsHandlers,
  ...tutorialHandlers,
  ...canvasOverlayHandlers,
  ...canvasCoverageHandlers,
} satisfies BridgeHandlers;
void _bridgeHandlersExhaustive;

/**
 * Register the global bridge receiver. Call once at app startup.
 *
 * @param onReady  invoked when Unity reports the project has loaded.
 * @param opts.assetSweep  install the asset-freshness reconcile sweep. Default false, and only the two
 *   pages hosted by TimelineWebViewPanel (the classic Timeline via EmbeddedLoader, and the scene board)
 *   pass true: it is the only Unity host that answers `requestAssetManifest`. The other four initBridge
 *   call sites — Screenplay, Screenplay Manager, Avatar Create, Avatar Customizer — run in webviews whose
 *   host has no responder, so a sweep there emits a verb that only ever lands in Unity's unhandled-action
 *   log and, once a responder exists per-host, would diff a manifest against a store that isn't a project.
 *   Note this is decided ONCE: initBridge early-returns on `bridgeReady`, so whichever page mounts first
 *   settles sweep installation for the life of that webview's JS context. A future route hosted by
 *   TimelineWebViewPanel must pass the flag itself — inheriting it from an earlier page is not a thing.
 */
export function initBridge(onReady?: () => void, opts?: { assetSweep?: boolean }): void {
  if (bridgeReady) return;
  bridgeReady = true;
  setOnProjectLoaded(onReady ?? null);

  // The handler TABLE is composed at module scope; assert it is complete before
  // exposing the receiver so a missing/unknown verb fails loudly in dev.
  assertBridgeTableComplete();
  (window as unknown as Record<string, unknown>).__previsRecv =
    (msg: unknown) => dispatchMessage(msg);

  // Created here rather than at its original site further down so the asset sweep below can share it:
  // one controller tears down every bridge-owned document listener.
  bridgeAbort = new AbortController();

  // Asset-freshness reconcile sweep: installs a visibility-change trigger only. No initial sweep is sent
  // at boot — boot is served by the normal load path; the sweep is a panel-show/visibility reconcile, not
  // a load-time fetch.
  if (opts?.assetSweep) {
    installAssetSweepTriggers(bridgeAbort.signal);
  }

  // Back button handler: Unity calls this before closing the panel.
  // If AI/Audio workspace is open, close it first. Otherwise close the panel.
  // If generation is active, show the confirmation dialog before closing workspace.
  (window as unknown as Record<string, unknown>).__requestBack = () => {
    // Shared with the tab shell's top-bar back arrow (SceneBoardPage) so the two back
    // affordances cannot drift. It delegates to the same actions the workspace X buttons
    // call, so this honors return-to-board (when the AI workspace was opened from the
    // board's Enhance button) and the generation-in-progress deferral. Returning here on
    // `true` is what stops the panel closing underneath that confirmation dialog.
    if (useUiStore.getState().closeOpenWorkspace()) {
      return;
    }

    // Telemetry: fire timeline_dismissed before the panel closes. Force-flush
    // happens inside the helper so the event reaches the server before
    // Unity destroys the WebView. Dedup-safe: pairs with the React-unmount
    // cleanup in TimelineView so whichever runs first wins.
    markTimelineDismissed();

    enqueueSendToNative('closePanel', {});
  };

  // Notify Unity when generation state changes (so it can disable export button)
  let prevGenerating = false;
  useAiStore.subscribe(state => {
    const generating = state.generatingImageForShot !== null || state.generatingVideoForShot !== null || state.transitionVideoGenerating !== null;
    if (generating !== prevGenerating) {
      prevGenerating = generating;
      enqueueSendToNative('generatingStateChanged', { generating });
    }
  });

  // Refresh credit balance when returning from external browser (e.g. Stripe checkout)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && useAccountStore.getState().nativeAccessToken) {
      useAccountStore.getState().refreshBalance();
    }
  }, { signal: bridgeAbort.signal });

  // Signal to native that the web app is ready to receive data. Still its OWN navigation rather than
  // an outbox message — native is not polling the outbox until something pings it, and `ready` is the
  // message that starts the session — but it now goes through the transport's guarded sender.
  //
  // 20260824 - This was a bare `window.location.href = …`, which is the one form this bridge cannot
  // use safely: unguarded, it neither waited for an in-flight navigation nor registered itself, so a
  // message enqueued just before it (any diagLog from a component mounting above this effect) fired a
  // ping over the top and WKWebView silently dropped `ready`. Unity gates PushProjectToWebView on
  // `ready`, so the whole project load never starts and the board opens blank. See
  // sendDirectNavigation in transport.ts for the full account. Do not inline a location.href here again.
  sendDirectNavigation('ready');
}

// ─── Static image persistence ────────────────────────────────────────────

/**
 * Send an AI-generated image to Unity for persistence as a static shot image.
 * Sends the full-quality image (no lossy recompression).
 * Uses chunking for images whose base64 exceeds 512 KB.
 * Fire-and-forget — does not block on Unity response.
 */
export async function sendStaticImageToUnity(shotId: number, imageDataUrl: string): Promise<void> {
  try {
    // Convert blob: URLs to data: URLs for bridge transport (no resize, no quality loss)
    let dataUrl = imageDataUrl;
    if (imageDataUrl.startsWith('blob:')) {
      const resp = await fetch(imageDataUrl);
      const blob = await resp.blob();
      dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }

    // Extract base64 portion to check size
    const commaIndex = dataUrl.indexOf(',');
    if (commaIndex < 0) return;
    const base64Data = dataUrl.substring(commaIndex + 1);

    // If under 512 KB base64, send as single message (fast path)
    const CHUNK_THRESHOLD = 512 * 1024;
    if (base64Data.length <= CHUNK_THRESHOLD) {
      enqueueSendToNative('setStaticImage', { shotId, imageDataUrl: dataUrl });
      return;
    }

    // Large image: chunk the base64 data
    const CHUNK_SIZE = 512 * 1024;
    const totalChunks = Math.ceil(base64Data.length / CHUNK_SIZE);
    const mimeType = dataUrl.substring(5, commaIndex).split(';')[0];

    for (let i = 0; i < totalChunks; i++) {
      const chunk = base64Data.substring(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      enqueueSendToNative('setStaticImageChunk', {
        shotId,
        mimeType,
        chunkIndex: i,
        totalChunks,
        data: chunk,
      });
    }
  } catch (err) {
    console.warn('[embeddedBridge] Failed to send static image to Unity:', err);
  }
}

// ─── Screenplay reimport ─────────────────────────────────────────────────

export { sendApplyReimport, sendCancelReimport, sendRequestImportNewVersion } from './features/screenplay';

// Re-export the sceneSettings senders so consumers (Scene Settings web modal) import
// from here unchanged, matching the other feature senders above.
export { sendRequestSceneSettings, sendApplySceneSettings, sendRequestFloorPreview, sendRequestSkyPreview, sendOpenLightingForScene, sendDeleteScene, sendInsertScene, sendDuplicateScene, sendOmitScene } from './features/sceneSettings';

// Re-export the shotSettings senders so consumers (Shot Settings web modal) import
// from here unchanged, matching the other feature senders above.
export { sendRequestShotSettings, sendApplyShotSettings, sendDeleteShot, sendCopyShot, sendInsertShot, sendPasteShot, sendCopyShots, sendDuplicateShots, sendDeleteShots } from './features/shotSettings';

// Re-export the projectSettings senders so consumers (the board shell's Project Settings
// modal) import from here unchanged, matching the other feature senders above.
export {
  sendRequestProjectSettings, sendApplyProjectSettings, sendLockProjectNumbering, sendRenumberProject,
  sendWatermarkLogoImage, sendDeleteWatermarkLogoImage, sendSetUseWatermarkLogoImage,
  sendRequestWatermarkLogoPreviews,
} from './features/projectSettings';
